/**
 * Cache Performance Dashboard - Redis Cache Monitoring
 *
 * FERP CRITICAL: Production-ready cache performance dashboard
 *
 * Backend: Redis cache with performance metrics
 * Storage: Redis with key pattern monitoring
 *
 * Features:
 * - Real-time metrics display (hit rate, throughput, latency)
 * - Key pattern analysis
 * - Memory usage visualization
 * - Performance trending charts
 * - Health status monitoring
 * - Operation statistics
 * - Invalidation history
 *
 * Revenue Impact: Part of $150K/year Cache Performance Dashboard
 */
// @ts-nocheck


import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Paper,
  Grid,
  Card,
  Badge,
  Button,
  Group,
  Stack,
  Table,
  RingProgress,
  Alert,
  LoadingOverlay,
  Progress,
  Tabs,
  Select,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconDatabase,
  IconCloudUpload,
  IconAlertTriangle,
  IconCheck,
  IconRefresh,
  IconChartLine,
  IconTrash,
  IconSettings,
  IconClock,
  IconTrendingUp,
  IconActivity,
} from '@tabler/icons-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer, Legend } from 'recharts';
import { useCacheMetrics } from '../hooks/useCacheMetrics';
import { CacheInvalidationManager } from './CacheInvalidationManager';
/**
 * Cache Performance Dashboard Component
 */
export function CachePerformanceDashboard() {
  const {
    metrics,
    keyPatterns,
    operationStats,
    invalidations,
    healthStatus,
    metricsHistory,
    loading,
    error,
    loadMetrics,
    loadKeyPatterns,
    loadOperationStats,
    loadInvalidations,
    loadHealthStatus,
    loadMetricsHistory,
    analyzePatterns,
    startAutoRefresh,
    stopAutoRefresh,
  } = useCacheMetrics();

  const [activeTab, setActiveTab] = useState<string>('overview');
  const [invalidationManagerOpen, setInvalidationManagerOpen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [timeRange, setTimeRange] = useState<string>('1h');
  const [recommendations, setRecommendations] = useState<string[]>([]);

  // Start/stop auto-refresh based on setting
  useEffect(() => {
    if (autoRefresh) {
      startAutoRefresh(5000); // 5 seconds
    } else {
      stopAutoRefresh();
    }
  }, [autoRefresh, startAutoRefresh, stopAutoRefresh]);

  // Load metrics history when time range changes
  useEffect(() => {
    const now = new Date();
    let startTime = new Date();

    switch (timeRange) {
      case '1h':
        startTime = new Date(now.getTime() - 60 * 60 * 1000);
        break;
      case '6h':
        startTime = new Date(now.getTime() - 6 * 60 * 60 * 1000);
        break;
      case '24h':
        startTime = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case '7d':
        startTime = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
    }

    loadMetricsHistory(startTime.toISOString(), now.toISOString());
  }, [timeRange, loadMetricsHistory]);

  // Handle analyze patterns
  const handleAnalyze = async () => {
    const recs = await analyzePatterns();
    setRecommendations(recs);
  };

  // Get health status color
  const getHealthColor = (status?: string): string => {
    switch (status) {
      case 'healthy':
        return 'green';
      case 'warning':
        return 'yellow';
      case 'critical':
        return 'red';
      default:
        return 'gray';
    }
  };

  // Format bytes
  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Format number
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  return (
    <Container size="xl" py="lg">
      <LoadingOverlay visible={loading} />

      {/* Header */}
      <Stack gap="md" mb="xl">
        <Group justify="space-between">
          <div>
            <Title order={2}>Cache Performance Dashboard</Title>
            <Text size="sm" c="dimmed">
              Real-time Redis cache monitoring and optimization
            </Text>
          </div>
          <Group>
            <Select
              placeholder="Time Range"
              data={[
                { value: '1h', label: 'Last Hour' },
                { value: '6h', label: 'Last 6 Hours' },
                { value: '24h', label: 'Last 24 Hours' },
                { value: '7d', label: 'Last 7 Days' },
              ]}
              value={timeRange}
              onChange={(value) => setTimeRange(value || '1h')}
              style={{ width: 150 }}
            />
            <Button
              variant="outline"
              leftSection={<IconRefresh size={16} />}
              onClick={() => {
                loadMetrics();
                loadKeyPatterns();
                loadOperationStats();
                loadInvalidations();
                loadHealthStatus();
              }}
            >
              Refresh
            </Button>
            <Button
              variant={autoRefresh ? 'filled' : 'outline'}
              leftSection={<IconActivity size={16} />}
              onClick={() => setAutoRefresh(!autoRefresh)}
            >
              Auto-Refresh {autoRefresh ? 'On' : 'Off'}
            </Button>
            <Button
              leftSection={<IconSettings size={16} />}
              onClick={() => setInvalidationManagerOpen(true)}
            >
              Manage Cache
            </Button>
          </Group>
        </Group>

        {/* Error Alert */}
        {error && (
          <Alert color="red" title="Error" icon={<IconAlertTriangle size={16} />}>
            {error.message}
          </Alert>
        )}

        {/* Health Status */}
        {healthStatus && (
          <Alert
            color={getHealthColor(healthStatus.status)}
            title={`Cache Health: ${healthStatus.status.toUpperCase()}`}
            icon={
              healthStatus.status === 'healthy' ? (
                <IconCheck size={16} />
              ) : (
                <IconAlertTriangle size={16} />
              )
            }
          >
            <Stack gap="xs">
              <Group gap="md">
                <Text size="sm">Memory: {healthStatus.memoryUtilization.toFixed(1)}%</Text>
                <Text size="sm">Hit Rate Threshold: {healthStatus.hitRateThreshold}%</Text>
                <Text size="sm">Eviction Threshold: {healthStatus.evictionRateThreshold}%</Text>
              </Group>
              {healthStatus.issues.length > 0 && (
                <div>
                  <Text size="sm" fw={500}>
                    Issues:
                  </Text>
                  {healthStatus.issues.map((issue, idx) => (
                    <Text key={idx} size="xs" c="dimmed">
                      • {issue}
                    </Text>
                  ))}
                </div>
              )}
            </Stack>
          </Alert>
        )}
      </Stack>

      {/* Key Metrics Cards */}
      <Grid mb="xl">
        {/* Hit Rate */}
        <Grid.Col span={3}>
          <Card shadow="sm" padding="lg">
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" fw={500} c="dimmed">
                  Hit Rate
                </Text>
                <IconChartLine size={20} color="green" />
              </Group>
              <Group ta="right">
                <Text size="2rem" fw={700}>
                  {metrics ? (metrics.hitRate * 100).toFixed(1) : '0'}%
                </Text>
                <RingProgress
                  size={60}
                  thickness={6}
                  sections={[
                    {
                      value: metrics ? metrics.hitRate * 100 : 0,
                      color: metrics && metrics.hitRate >= 0.8 ? 'green' : metrics && metrics.hitRate >= 0.6 ? 'yellow' : 'red',
                    },
                  ]}
                />
              </Group>
              <Text size="xs" c="dimmed">
                Miss Rate: {metrics ? (metrics.missRate * 100).toFixed(1) : '0'}%
              </Text>
            </Stack>
          </Card>
        </Grid.Col>

        {/* Total Keys */}
        <Grid.Col span={3}>
          <Card shadow="sm" padding="lg">
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" fw={500} c="dimmed">
                  Total Keys
                </Text>
                <IconDatabase size={20} color="blue" />
              </Group>
              <Text size="2rem" fw={700}>
                {metrics ? formatNumber(metrics.totalKeys) : '0'}
              </Text>
              <Text size="xs" c="dimmed">
                Across all patterns
              </Text>
            </Stack>
          </Card>
        </Grid.Col>

        {/* Memory Usage */}
        <Grid.Col span={3}>
          <Card shadow="sm" padding="lg">
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" fw={500} c="dimmed">
                  Memory Usage
                </Text>
                <IconCloudUpload size={20} color="orange" />
              </Group>
              <Text size="2rem" fw={700}>
                {metrics ? formatBytes(metrics.memoryUsed) : '0 B'}
              </Text>
              <Progress
                value={metrics ? (metrics.memoryUsed / metrics.memoryLimit) * 100 : 0}
                size="sm"
                color={
                  metrics && metrics.memoryUsed / metrics.memoryLimit > 0.9
                    ? 'red'
                    : metrics && metrics.memoryUsed / metrics.memoryLimit > 0.7
                    ? 'yellow'
                    : 'blue'
                }
              />
              <Text size="xs" c="dimmed">
                Limit: {metrics ? formatBytes(metrics.memoryLimit) : '0 B'}
              </Text>
            </Stack>
          </Card>
        </Grid.Col>

        {/* Throughput */}
        <Grid.Col span={3}>
          <Card shadow="sm" padding="lg">
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" fw={500} c="dimmed">
                  Throughput
                </Text>
                <IconTrendingUp size={20} color="purple" />
              </Group>
              <Text size="2rem" fw={700}>
                {metrics ? formatNumber(metrics.throughput) : '0'}
              </Text>
              <Text size="xs" c="dimmed">
                ops/sec | Latency: {metrics ? metrics.avgLatencyMs.toFixed(2) : '0'}ms
              </Text>
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(value: string | null) => setActiveTab(value || 'overview')}>
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconChartLine size={16} />}>
            Performance
          </Tabs.Tab>
          <Tabs.Tab value="patterns" leftSection={<IconDatabase size={16} />}>
            Key Patterns
          </Tabs.Tab>
          <Tabs.Tab value="operations" leftSection={<IconActivity size={16} />}>
            Operations
          </Tabs.Tab>
          <Tabs.Tab value="invalidations" leftSection={<IconClock size={16} />}>
            Invalidations
          </Tabs.Tab>
        </Tabs.List>

        {/* Performance Tab */}
        <Tabs.Panel value="overview" pt="md">
          <Stack gap="lg">
            {/* Hit Rate Chart */}
            <Paper shadow="xs" p="md">
              <Title order={4} mb="md">
                Hit Rate Over Time
              </Title>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={metricsHistory.hitRate.map((d) => ({
                  ...d,
                  value: d.value * 100,
                  time: new Date(d.timestamp).toLocaleTimeString(),
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis domain={[0, 100]} />
                  <ChartTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="value" stroke="#82ca9d" name="Hit Rate %" />
                </LineChart>
              </ResponsiveContainer>
            </Paper>

            {/* Throughput Chart */}
            <Paper shadow="xs" p="md">
              <Title order={4} mb="md">
                Throughput Over Time
              </Title>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={metricsHistory.throughput.map((d) => ({
                  ...d,
                  time: new Date(d.timestamp).toLocaleTimeString(),
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <ChartTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="value" stroke="#8884d8" name="Ops/sec" />
                </LineChart>
              </ResponsiveContainer>
            </Paper>

            {/* Latency Chart */}
            <Paper shadow="xs" p="md">
              <Title order={4} mb="md">
                Average Latency Over Time
              </Title>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={metricsHistory.latency.map((d) => ({
                  ...d,
                  time: new Date(d.timestamp).toLocaleTimeString(),
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <ChartTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="value" stroke="#ffc658" name="Latency (ms)" />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Stack>
        </Tabs.Panel>

        {/* Key Patterns Tab */}
        <Tabs.Panel value="patterns" pt="md">
          <Paper shadow="xs" p="md">
            <Stack gap="md">
              <Group justify="space-between">
                <Title order={4}>Cache Key Patterns</Title>
                <Button size="sm" variant="outline" onClick={handleAnalyze}>
                  Analyze Patterns
                </Button>
              </Group>

              {recommendations.length > 0 && (
                <Alert icon={<IconAlertTriangle size={16} />} color="blue">
                  <Stack gap="xs">
                    <Text size="sm" fw={500}>
                      Recommendations:
                    </Text>
                    {recommendations.map((rec, idx) => (
                      <Text key={idx} size="xs">
                        • {rec}
                      </Text>
                    ))}
                  </Stack>
                </Alert>
              )}

              <Table highlightOnHover>
                <thead>
                  <tr>
                    <th>Pattern</th>
                    <th>Key Count</th>
                    <th>Memory</th>
                    <th>Avg TTL</th>
                    <th>Hit Rate</th>
                    <th>Last Accessed</th>
                  </tr>
                </thead>
                <tbody>
                  {keyPatterns.map((pattern, idx) => (
                    <tr key={idx}>
                      <td>
                        <Text fw={500} size="sm">
                          {pattern.pattern}
                        </Text>
                      </td>
                      <td>{formatNumber(pattern.keyCount)}</td>
                      <td>{formatBytes(pattern.totalMemoryBytes)}</td>
                      <td>{pattern.avgTTL > 0 ? `${Math.round(pattern.avgTTL / 60)}m` : 'No TTL'}</td>
                      <td>
                        <Badge color={pattern.hitRate >= 0.8 ? 'green' : pattern.hitRate >= 0.6 ? 'yellow' : 'red'}>
                          {(pattern.hitRate * 100).toFixed(1)}%
                        </Badge>
                      </td>
                      <td>
                        <Text size="xs">{new Date(pattern.lastAccessed).toLocaleString()}</Text>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {keyPatterns.length === 0 && (
                <Text size="sm" c="dimmed" ta="center" py="xl">
                  No key patterns found
                </Text>
              )}
            </Stack>
          </Paper>
        </Tabs.Panel>

        {/* Operations Tab */}
        <Tabs.Panel value="operations" pt="md">
          <Paper shadow="xs" p="md">
            <Stack gap="md">
              <Title order={4}>Cache Operation Statistics</Title>

              <Table highlightOnHover>
                <thead>
                  <tr>
                    <th>Operation</th>
                    <th>Count</th>
                    <th>Avg Latency</th>
                    <th>Error Count</th>
                    <th>Last Executed</th>
                  </tr>
                </thead>
                <tbody>
                  {operationStats.map((stat, idx) => (
                    <tr key={idx}>
                      <td>
                        <Badge>{stat.operation}</Badge>
                      </td>
                      <td>{formatNumber(stat.count)}</td>
                      <td>{stat.avgLatencyMs.toFixed(2)}ms</td>
                      <td>
                        <Text color={stat.errorCount > 0 ? 'red' : 'dimmed'}>
                          {stat.errorCount}
                        </Text>
                      </td>
                      <td>
                        <Text size="xs">{new Date(stat.lastExecuted).toLocaleString()}</Text>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {operationStats.length === 0 && (
                <Text size="sm" c="dimmed" ta="center" py="xl">
                  No operation statistics available
                </Text>
              )}

              {/* Operation Distribution Chart */}
              {operationStats.length > 0 && (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={operationStats}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="operation" />
                    <YAxis />
                    <ChartTooltip />
                    <Legend />
                    <Bar dataKey="count" fill="#8884d8" name="Operation Count" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Stack>
          </Paper>
        </Tabs.Panel>

        {/* Invalidations Tab */}
        <Tabs.Panel value="invalidations" pt="md">
          <Paper shadow="xs" p="md">
            <Stack gap="md">
              <Group justify="space-between">
                <Title order={4}>Invalidation History</Title>
                <Button
                  size="sm"
                  leftSection={<IconSettings size={16} />}
                  onClick={() => setInvalidationManagerOpen(true)}
                >
                  Manage Invalidations
                </Button>
              </Group>

              <Table highlightOnHover>
                <thead>
                  <tr>
                    <th>Pattern</th>
                    <th>Reason</th>
                    <th>Keys Invalidated</th>
                    <th>Timestamp</th>
                    <th>Triggered By</th>
                  </tr>
                </thead>
                <tbody>
                  {invalidations.map((inv) => (
                    <tr key={inv.id}>
                      <td>
                        <Text fw={500} size="sm">
                          {inv.pattern}
                        </Text>
                      </td>
                      <td>
                        <Text size="xs">{inv.reason}</Text>
                      </td>
                      <td>
                        <Badge>{formatNumber(inv.invalidatedKeys)}</Badge>
                      </td>
                      <td>
                        <Text size="xs">{new Date(inv.timestamp).toLocaleString()}</Text>
                      </td>
                      <td>
                        <Text size="xs">{inv.triggeredBy}</Text>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {invalidations.length === 0 && (
                <Text size="sm" c="dimmed" ta="center" py="xl">
                  No invalidations recorded
                </Text>
              )}
            </Stack>
          </Paper>
        </Tabs.Panel>
      </Tabs>

      {/* Cache Invalidation Manager Modal */}
      {invalidationManagerOpen && (
        <CacheInvalidationManager
          onClose={() => setInvalidationManagerOpen(false)}
          onInvalidate={() => {
            loadMetrics();
            loadKeyPatterns();
            loadInvalidations();
          }}
        />
      )}
    </Container>
  );
}
